import { useState, useEffect, useRef, type FC } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Fade from '@mui/material/Fade';
import Skeleton from '@mui/material/Skeleton';
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  NotificationImportant as PagerDutyIcon,
  Source as SourceIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { PagerDutyIntegrationCardProps, PagerDutyData } from './types';
import { usePagerDutyData } from './usePagerDutyData';

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: `2px solid ${theme.palette.primary.main}`,
}));

const SectionContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 0,
  padding: theme.spacing(1, 0),
  // flatten row appearance so multiple rows visually belong to the same section
  backgroundColor: 'transparent',
  borderRadius: 0,
  border: 'none',
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: 'background-color 0.15s ease-in-out, transform 0.15s',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const DetailContent = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
}));

const DetailValue = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontSize: '0.95rem',
}));

const SourceChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.75rem',
  height: 24,
  backgroundColor: theme.palette.info.light,
  color: theme.palette.info.contrastText,
  fontWeight: 500,
  '& .MuiChip-icon': {
    fontSize: '0.9rem',
  },
}));

const AnnotationContainer = styled(Box)(({ theme }) => ({
  // Use paper background so the annotation preview is light/white instead of dark
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
  overflow: 'hidden',
}));

const AnnotationKey = styled(Typography)(({ theme }) => ({
  fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
}));

const EditableRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const AnnotationValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  backgroundColor: 'transparent',
  padding: theme.spacing(0.5),
  borderRadius: theme.spacing(0.5),
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      border: `1px solid ${theme.palette.grey[600]}`,
    },
    '&:hover fieldset': {
      border: `1px solid ${theme.palette.primary.main}`,
    },
    '&.Mui-focused fieldset': {
      border: `2px solid ${theme.palette.primary.main}`,
    },
  },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.grey[400],
  backgroundColor: theme.palette.grey[800],
  width: 32,
  height: 32,
  '&:hover': {
    backgroundColor: theme.palette.grey[700],
    color: theme.palette.primary.main,
  },
  '&.save': {
    color: theme.palette.success.main,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
      color: theme.palette.success.contrastText,
    },
  },
  '&.cancel': {
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
      color: theme.palette.error.contrastText,
    },
  },
}));

export const PagerDutyIntegrationCard: FC<PagerDutyIntegrationCardProps> = ({
  selectedRepository,
  data: sharedData,
  onDataUpdate,
}) => {
  const { data: hookData, loading } = usePagerDutyData(selectedRepository);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const initializedRef = useRef(false);
  const lastRepositoryRef = useRef<string>('');

  // Use shared data if available, otherwise use hook data
  const data = sharedData || hookData;

  // Reset initialization flag when repository changes
  useEffect(() => {
    if (lastRepositoryRef.current !== selectedRepository) {
      initializedRef.current = false;
      lastRepositoryRef.current = selectedRepository;
    }
  }, [selectedRepository]);

  // Initialize shared state when hook data is loaded (only once per repository)
  useEffect(() => {
    if (hookData && !sharedData && onDataUpdate && !initializedRef.current) {
      initializedRef.current = true;
      onDataUpdate(hookData);
    }
  }, [hookData, sharedData, onDataUpdate]);

  // Don't render if no repository is selected
  if (!selectedRepository) {
    return null;
  }

  // Construct the annotation value with dynamic URL
  const serviceId = data?.serviceId || '';
  const annotationValue = serviceId;

  const handleEditStart = () => {
    setEditValue(serviceId);
    setIsEditing(true);
  };

  const handleEditSave = () => {
    if (onDataUpdate) {
      // Handle empty editValue - remove the data by passing null
      if (!editValue || editValue.trim() === '') {
        onDataUpdate(null as any); // Remove the data which will hide the annotation
        setIsEditing(false);
        return;
      }

      if (data) {
        // Update existing data
        const updatedData: PagerDutyData = {
          ...data,
          serviceId: editValue.trim(),
        };
        onDataUpdate(updatedData);
      } else {
        // Create new data when none exists
        const newData: PagerDutyData = {
          provider: 'pagerduty', // Default provider
          serviceId: editValue.trim(),
          source: 'manual', // Mark as manually added
        };
        onDataUpdate(newData);
      }
    }
    setIsEditing(false);
  };

  const handleEditCancel = () => {
    setEditValue('');
    setIsEditing(false);
  };

  // Show loading state with skeleton
  if (loading) {
    return (
      <Fade in timeout={600}>
        <StyledCard>
          <CardContent sx={{ p: 3 }}>
            <CardHeader>
              <PagerDutyIcon color="primary" sx={{ fontSize: 28 }} />
              <Typography variant="h5" fontWeight={600} color="primary">
                PagerDuty Integration
              </Typography>
            </CardHeader>

            <SectionContainer elevation={0}>
              <SectionHeader>
                <Skeleton variant="text" width="40%" height={28} />
              </SectionHeader>

              {/* Loading skeleton for input */}
              <Box sx={{ mb: 2 }}>
                <Skeleton variant="text" width="30%" height={20} />
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={56}
                  sx={{ mt: 1, borderRadius: 1 }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <SectionHeader sx={{ mt: 1 }}>
                <Skeleton variant="text" width="35%" height={28} />
              </SectionHeader>

              <Box
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Skeleton variant="text" width="40%" height={20} />
                <Skeleton
                  variant="text"
                  width="70%"
                  height={24}
                  sx={{ mt: 1 }}
                />
              </Box>
            </SectionContainer>
          </CardContent>
        </StyledCard>
      </Fade>
    );
  }

  return (
    <Fade in timeout={600}>
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <CardHeader>
            <PagerDutyIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} color="primary">
              PagerDuty Integration
            </Typography>
          </CardHeader>

          {/* PagerDuty Input Section */}
          <SectionContainer elevation={0}>
            <SectionHeader>
              <SourceIcon color="action" />
              <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
                PagerDuty Service Configuration
              </Typography>
              <LinkIcon color="action" />
            </SectionHeader>

            <DetailRow>
              <DetailContent>
                <DetailValue>PagerDuty Service ID</DetailValue>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 0.5 }}
                >
                  {data?.serviceId || 'No PagerDuty Service Id discovered'}
                </Typography>
              </DetailContent>
              {data?.source && (
                <SourceChip
                  icon={<SourceIcon />}
                  label={`Source: ${data.source}`}
                  size="small"
                  variant="filled"
                />
              )}
            </DetailRow>

            <Divider sx={{ my: 2 }} />

            <SectionHeader sx={{ mt: 1 }}>
              <LinkIcon color="action" />
              <Typography variant="h6" fontWeight={600}>
                Annotation Preview
              </Typography>
            </SectionHeader>

            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mb: 2, lineHeight: 1.5 }}
            >
              Add the PagerDuty service Id URL (e.g.{' '}
              <Typography
                component="span"
                fontFamily="Monaco, Menlo, 'Ubuntu Mono', monospace"
                fontSize="0.8rem"
              >
                https://mercedesbenzio.eu.pagerduty.com/service-directory/&lt;PagerDuty
                ID&gt;
              </Typography>
              )
            </Typography>

            <AnnotationContainer sx={{ mt: 1, mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  width: '100%',
                }}
              >
                <AnnotationKey
                  sx={{ mr: 1, whiteSpace: 'nowrap', marginBottom: 0 }}
                >
                  datadoghq.mercedes-benz.com/pagerduty:
                </AnnotationKey>
                <Box sx={{ flex: 1 }}>
                  <EditableRow sx={{ width: '100%' }}>
                    {isEditing ? (
                      <>
                        <StyledTextField
                          size="small"
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          variant="outlined"
                          placeholder="Enter PagerDuty Service ID"
                          fullWidth
                        />
                        <ActionButton
                          size="small"
                          onClick={handleEditSave}
                          className="save"
                        >
                          <CheckIcon />
                        </ActionButton>
                        <ActionButton
                          size="small"
                          onClick={handleEditCancel}
                          className="cancel"
                        >
                          <CloseIcon />
                        </ActionButton>
                      </>
                    ) : (
                      <>
                        <AnnotationValue onClick={handleEditStart}>
                          {annotationValue}
                        </AnnotationValue>
                        <ActionButton size="small" onClick={handleEditStart}>
                          <EditIcon />
                        </ActionButton>
                      </>
                    )}
                  </EditableRow>
                </Box>
              </Box>
            </AnnotationContainer>
          </SectionContainer>
        </CardContent>
      </StyledCard>
    </Fade>
  );
};
